import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Form, Button, Row, Col, Badge, OverlayTrigger, Popover } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { LICHHUONGDANTHUCHANH_API, LOPHUONGDANTHUCHANH_TRIENKHAI_API, DONVI_API, ACCOUNT_AUTHINFO_API } from '../../../../utils/apiUrl';
import { Get, DeleteSingle } from '../../../../utils/httpProvider';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import { getUser } from '../../../../utils/oidc';
import CreateScheduleThucHanhModal from './CreateScheduleThucHanhModal';
import EditScheduleThucHanhModal from './EditScheduleThucHanhModal';

// Helper gom nhóm các buổi học ngắt quãng thành một giai đoạn đào tạo duy nhất
const groupSchedules = (list) => {
    if (!list || list.length === 0) return [];
    const map = new Map();

    list.forEach(item => {
        let groupKey;
        const bId = item.batchId || item.nganhDaoTaoId;
        const hv = item.hoSoTuyenSinhId || item.hocVienTen || '';
        if (bId && bId !== '00000000-0000-0000-0000-000000000000') {
            groupKey = `batch_${bId}_${hv}`;
        } else {
            // Fallback gom nhóm cho dữ liệu cũ chưa có batchId
            const donVi = (item.donVi || '').trim();
            const gv = item.giangVienId || item.giangVienTen || (item.giangViens && item.giangViens[0]?.giangVienTen) || '';
            const td = (item.trinhDo || '').trim();
            const gc = (item.ghiChu || '').trim();
            groupKey = `comp_${hv}_${donVi}_${gv}_${td}_${gc}`;
        }

        if (!map.has(groupKey)) {
            map.set(groupKey, {
                key: groupKey,
                items: [],
                representative: item,
                hoSoTuyenSinhId: item.hoSoTuyenSinhId,
                hocVienTen: item.hocVienTen,
                donVi: item.donVi,
                giangVienTen: item.giangVienTen,
                giangViens: item.giangViens,
                trinhDo: item.trinhDo,
                ghiChu: item.ghiChu,
                trangThai: item.trangThai,
                batchId: bId || null,
            });
        }
        map.get(groupKey).items.push(item);
    });

    return Array.from(map.values()).map(group => {
        group.items.sort((a, b) => new Date(a.ngayBatDau) - new Date(b.ngayBatDau));
        const dates = group.items.map(it => moment(it.ngayBatDau).format('DD/MM/YYYY')).filter(Boolean);
        const isSingleRange = group.items.length === 1 && group.items[0].ngayKetThuc && group.items[0].ngayKetThuc !== group.items[0].ngayBatDau;
        return {
            ...group,
            dates,
            isSingleRange,
            minDate: dates[0] || '',
            maxDate: dates[dates.length - 1] || ''
        };
    });
};

const ScheduleThucHanhTab = ({ lopId }) => {
    const queryClient = useQueryClient();

    // 0. Fetch Current Logged-in User
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        getUser().then(u => {
            if (u?.profile) setCurrentUser(u.profile);
        });
    }, []);

    const currentUserId = currentUser?.sub || currentUser?.id || '';

    // View state: 'matrix' (default) | 'list' | 'student' | 'instructor'
    const [viewMode, setViewMode] = useState('matrix');

    // Matrix target for Admin: 'student' (Ma trận học viên) | 'instructor' (Ma trận giảng viên)
    const [matrixTarget, setMatrixTarget] = useState('student');

    // Quick create state from matrix cell click
    const [quickCreateState, setQuickCreateState] = useState({
        studentId: null,
        date: null
    });

    // Matrix view month/year
    const [thang, setThang] = useState(moment().month() + 1);
    const [nam, setNam] = useState(moment().year());

    // Search and filters
    const [studentFilter, setStudentFilter] = useState('');
    const [tutorFilter, setTutorFilter] = useState('');
    const [donViFilter, setDonViFilter] = useState('');
    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [selectedInstructorId, setSelectedInstructorId] = useState('');

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // 1. Fetch Students in Class
    const { data: studentsData = [] } = useQuery(
        ['studentsThucHanh', lopId],
        () => Get(`${LOPHUONGDANTHUCHANH_TRIENKHAI_API.GET_HOCVIENS}/${lopId}`),
        {
            enabled: !!lopId,
            select: (res) => res?.$values || res || []
        }
    );

    // 2. Fetch Tutors in Class with SSO Profile
    const { data: tutorsData = [] } = useQuery(
        ['giangViensThucHanh', lopId],
        async () => {
            const res = await Get(`${LOPHUONGDANTHUCHANH_TRIENKHAI_API.GET_GIANGVIENS}/${lopId}`);
            const accountIds = res?.$values || res || [];

            const detailTasks = accountIds.map(async (accountId) => {
                try {
                    const accountInfo = await Get(ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT, accountId);
                    const profile = accountInfo?.accountAuthInfo || accountInfo?.AccountAuthInfo || accountInfo;
                    const lastName = profile?.lastName || profile?.LastName || "";
                    const middleName = profile?.middleName || profile?.MiddleName || "";
                    const firstName = profile?.firstName || profile?.FirstName || "";
                    const fullName = [lastName, middleName, firstName].map(n => n?.trim()).filter(Boolean).join(" ");

                    return {
                        id: accountId,
                        accountId: accountId,
                        fullName: fullName || "Chưa xác định",
                        identityCard: profile?.identityCardNumber || profile?.IdentityCardNumber || profile?.identityCard || profile?.IdentityCard || ""
                    };
                } catch {
                    return { id: accountId, accountId, fullName: "Giảng viên" };
                }
            });

            return await Promise.all(detailTasks);
        },
        { enabled: !!lopId }
    );

    // Xác định vai trò người dùng hiện tại trong lớp
    const currentStudent = useMemo(() => {
        if (!currentUserId || studentsData.length === 0) return null;
        return studentsData.find(s => s.accountId === currentUserId || s.id === currentUserId) || null;
    }, [studentsData, currentUserId]);

    const currentTutor = useMemo(() => {
        if (!currentUserId || tutorsData.length === 0) return null;
        return tutorsData.find(t => t.accountId === currentUserId || t.id === currentUserId) || null;
    }, [tutorsData, currentUserId]);

    const isStudent = !!currentStudent;
    const isInstructor = !!currentTutor && !isStudent;
    const isAdmin = !isStudent && !isInstructor;

    const studentOptions = useMemo(() => {
        return studentsData.map(s => ({
            value: s.id,
            label: `${s.hoTen || 'Học viên'}${s.soCccd ? ` (${s.soCccd})` : ''}`
        }));
    }, [studentsData]);

    const instructorOptions = useMemo(() => {
        return tutorsData.map(t => ({
            value: t.id || t.accountId,
            label: `${t.fullName || 'Giảng viên'}${t.identityCard ? ` (${t.identityCard})` : ''}`
        }));
    }, [tutorsData]);

    // Tự động gán ID mặc định và viewMode theo vai trò
    useEffect(() => {
        if (isStudent && currentStudent) {
            setViewMode('matrix');
            setSelectedStudentId(currentStudent.id);
        } else if (isInstructor && currentTutor) {
            setViewMode('matrix');
            setSelectedInstructorId(currentTutor.id || currentTutor.accountId);
        } else if (isAdmin) {
            if (studentsData.length > 0 && !selectedStudentId) {
                setSelectedStudentId(studentsData[0].id);
            }
            if (tutorsData.length > 0 && !selectedInstructorId) {
                setSelectedInstructorId(tutorsData[0].id || tutorsData[0].accountId);
            }
        }
    }, [isStudent, currentStudent, isInstructor, currentTutor, isAdmin, studentsData, tutorsData]);

    // 3. Fetch Departments (Khoa / Đơn vị)
    const { data: donViList = [] } = useQuery(
        'donViListThucHanh',
        () => Get(DONVI_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }),
        {
            select: (res) => res?.data?.data || res?.data || res || []
        }
    );

    // 4. Fetch Detailed Schedule List (Dành cho Quản trị viên xem toàn lớp)
    const {
        data: scheduleList = [],
        isLoading: isScheduleLoading,
        refetch: refetchSchedule
    } = useQuery(
        ['scheduleListThucHanh', lopId],
        () => Get(`${LICHHUONGDANTHUCHANH_API.GET_BY_COURSE}/${lopId}`),
        {
            enabled: !!lopId && (viewMode === 'list' || viewMode === 'matrix'),
            select: (res) => res?.$values || res?.data?.$values || res?.data || res || []
        }
    );

    // 4.1. Fetch Lịch Giảng Viên Cá Nhân (Sử dụng API GetLichGiangVienCaNhan)
    const {
        data: instructorScheduleList = [],
        isLoading: isInstructorLoading,
        refetch: refetchInstructorSchedule
    } = useQuery(
        ['instructorScheduleThucHanh', lopId, selectedInstructorId],
        () => Get(`${LICHHUONGDANTHUCHANH_API.GET_LICH_GIANG_VIEN_CA_NHAN}/${lopId}${selectedInstructorId ? `?giangVienId=${selectedInstructorId}` : ''}`),
        {
            enabled: !!lopId && viewMode === 'instructor',
            select: (res) => res?.$values || res?.data?.$values || res?.data || res || []
        }
    );

    // 4.2. Fetch Lịch Học Viên Cá Nhân (Sử dụng API GetLichHocVienCaNhan)
    const {
        data: studentScheduleList = [],
        isLoading: isStudentLoading,
        refetch: refetchStudentSchedule
    } = useQuery(
        ['studentScheduleThucHanh', lopId, selectedStudentId],
        () => Get(`${LICHHUONGDANTHUCHANH_API.GET_LICH_HOC_VIEN_CA_NHAN}/${lopId}${selectedStudentId ? `?hocVienId=${selectedStudentId}` : ''}`),
        {
            enabled: !!lopId && viewMode === 'student',
            select: (res) => res?.$values || res?.data?.$values || res?.data || res || []
        }
    );

    // 5. Fetch Matrix Schedule cho Quản trị viên theo Học Viên (GET_ADMIN_MATRIX)
    const {
        data: adminMatrixData,
        isLoading: isAdminMatrixLoading,
        refetch: refetchAdminMatrix
    } = useQuery(
        ['adminMatrixThucHanh', lopId, thang, nam],
        () => Get(`${LICHHUONGDANTHUCHANH_API.GET_ADMIN_MATRIX}/${lopId}?thang=${thang}&nam=${nam}`),
        {
            enabled: !!lopId && viewMode === 'matrix' && (!isAdmin || matrixTarget === 'student'),
            select: (res) => res?.data || res
        }
    );

    // 5.0. Fetch Matrix Schedule cho Quản trị viên theo Giảng Viên (GET_ADMIN_INSTRUCTOR_MATRIX)
    const {
        data: adminInstructorMatrixData,
        isLoading: isAdminInstructorMatrixLoading,
        refetch: refetchAdminInstructorMatrix
    } = useQuery(
        ['adminInstructorMatrixThucHanh', lopId, thang, nam],
        () => Get(`${LICHHUONGDANTHUCHANH_API.GET_ADMIN_INSTRUCTOR_MATRIX}/${lopId}?thang=${thang}&nam=${nam}`),
        {
            enabled: !!lopId && viewMode === 'matrix' && isAdmin && matrixTarget === 'instructor',
            select: (res) => res?.data || res
        }
    );

    // 5.1. Fetch Matrix Schedule cho Học Viên Cá Nhân (GET_MATRIX_HOC_VIEN_CA_NHAN)
    const {
        data: studentMatrixData,
        isLoading: isStudentMatrixLoading,
        refetch: refetchStudentMatrix
    } = useQuery(
        ['studentMatrixThucHanh', lopId, thang, nam, selectedStudentId],
        () => Get(`${LICHHUONGDANTHUCHANH_API.GET_MATRIX_HOC_VIEN_CA_NHAN}/${lopId}?thang=${thang}&nam=${nam}${selectedStudentId ? `&hocVienId=${selectedStudentId}` : ''}`),
        {
            enabled: !!lopId && (viewMode === 'student' || (viewMode === 'matrix' && isStudent)),
            select: (res) => res?.data || res
        }
    );

    // 5.2. Fetch Matrix Schedule cho Giảng Viên Cá Nhân (GET_MATRIX_GIANG_VIEN_CA_NHAN)
    const {
        data: instructorMatrixData,
        isLoading: isInstructorMatrixLoading,
        refetch: refetchInstructorMatrix
    } = useQuery(
        ['instructorMatrixThucHanh', lopId, thang, nam, selectedInstructorId],
        () => Get(`${LICHHUONGDANTHUCHANH_API.GET_MATRIX_GIANG_VIEN_CA_NHAN}/${lopId}?thang=${thang}&nam=${nam}${selectedInstructorId ? `&giangVienId=${selectedInstructorId}` : ''}`),
        {
            enabled: !!lopId && (viewMode === 'instructor' || (viewMode === 'matrix' && isInstructor)),
            select: (res) => res?.data || res
        }
    );

    const matrixData = isStudent 
        ? studentMatrixData 
        : isInstructor 
            ? instructorMatrixData 
            : (matrixTarget === 'instructor' ? adminInstructorMatrixData : adminMatrixData);

    const isMatrixLoading = isStudent 
        ? isStudentMatrixLoading 
        : isInstructor 
            ? isInstructorMatrixLoading 
            : (matrixTarget === 'instructor' ? isAdminInstructorMatrixLoading : isAdminMatrixLoading);

    // Handle Delete Single Row
    const handleConfirmDelete = async () => {
        if (!itemToDelete?.id) return;
        try {
            await DeleteSingle(LICHHUONGDANTHUCHANH_API.DELETE_ITEM, itemToDelete.id);
            toast.success('Đã xóa dòng lịch học thành công.');
            refetchSchedule();
            refetchInstructorSchedule();
            refetchStudentSchedule();
            refetchAdminMatrix();
            refetchAdminInstructorMatrix();
            refetchStudentMatrix();
            refetchInstructorMatrix();
        } catch (err) {
            toast.error(err?.message || 'Không thể xóa dòng lịch học.');
        } finally {
            setShowDeleteModal(false);
            setItemToDelete(null);
        }
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setShowEditModal(true);
    };

    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const handleRefetchAll = () => {
        refetchSchedule();
        refetchInstructorSchedule();
        refetchStudentSchedule();
        refetchAdminMatrix();
        refetchAdminInstructorMatrix();
        refetchStudentMatrix();
        refetchInstructorMatrix();
    };

    // Filtered Schedule List
    const filteredScheduleList = scheduleList.filter(item => {
        const matchStudent = !studentFilter || (item.hocVienTen && item.hocVienTen.toLowerCase().includes(studentFilter.toLowerCase().trim()));
        const gvTen = item.giangVienTen || (item.giangViens && item.giangViens.map(g => g.giangVienTen).join(' ')) || '';
        const matchTutor = !tutorFilter || gvTen.toLowerCase().includes(tutorFilter.toLowerCase().trim());
        const matchDonVi = !donViFilter || (item.donVi && item.donVi.toLowerCase().includes(donViFilter.toLowerCase().trim()));
        return matchStudent && matchTutor && matchDonVi;
    });

    // Matrix dates & rows (được trả về chuẩn từ Backend)
    const matrixDates = matrixData?.dates?.$values || matrixData?.dates || [];
    const matrixRows = (matrixData?.rows?.$values || matrixData?.rows || []).filter(r => {
        const name = r.hocVienTen || '';
        if (isAdmin && matrixTarget === 'instructor') {
            return !tutorFilter || name.toLowerCase().includes(tutorFilter.toLowerCase().trim());
        }
        return !studentFilter || name.toLowerCase().includes(studentFilter.toLowerCase().trim());
    });

    // Dữ liệu gom nhóm đồng bộ cho các views
    const groupedScheduleList = useMemo(() => groupSchedules(scheduleList), [scheduleList]);
    const groupedFilteredScheduleList = useMemo(() => groupSchedules(filteredScheduleList), [filteredScheduleList]);

    // Student Timeline data (Sử dụng dữ liệu từ API GetLichHocVienCaNhan)
    const studentSchedules = useMemo(() => {
        if (!thang || !nam) return studentScheduleList;
        return studentScheduleList.filter(item => {
            const start = moment(item.ngayBatDau);
            const end = item.ngayKetThuc ? moment(item.ngayKetThuc) : start;
            const targetMonthStart = moment(`${nam}-${String(thang).padStart(2, '0')}-01`, 'YYYY-MM-DD').startOf('month');
            const targetMonthEnd = moment(targetMonthStart).endOf('month');
            return start.isSameOrBefore(targetMonthEnd) && end.isSameOrAfter(targetMonthStart);
        });
    }, [studentScheduleList, thang, nam]);
    const groupedStudentSchedules = useMemo(() => groupSchedules(studentSchedules), [studentSchedules]);

    // Instructor Agenda data (Sử dụng dữ liệu từ API GetLichGiangVienCaNhan)
    const instructorSchedules = useMemo(() => {
        if (!thang || !nam) return instructorScheduleList;
        return instructorScheduleList.filter(item => {
            const start = moment(item.ngayBatDau);
            const end = item.ngayKetThuc ? moment(item.ngayKetThuc) : start;
            const targetMonthStart = moment(`${nam}-${String(thang).padStart(2, '0')}-01`, 'YYYY-MM-DD').startOf('month');
            const targetMonthEnd = moment(targetMonthStart).endOf('month');
            return start.isSameOrBefore(targetMonthEnd) && end.isSameOrAfter(targetMonthStart);
        });
    }, [instructorScheduleList, thang, nam]);
    const groupedInstructorSchedules = useMemo(() => groupSchedules(instructorSchedules), [instructorSchedules]);

    return (
        <div>
            {/* TOOLBAR: VIEW SWITCHER + ACTION BUTTONS */}
            <div className="d-flex flex-column flex-xl-row justify-content-between align-items-stretch align-items-xl-center mb-3 p-2 bg-white rounded-lg border shadow-sm" style={{ gap: '10px' }}>
                <div className="btn-group flex-nowrap overflow-auto" role="group">
                    <Button
                        variant={viewMode === 'matrix' ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => setViewMode('matrix')}
                        style={{ 
                            borderTopLeftRadius: '6px', 
                            borderBottomLeftRadius: '6px', 
                            whiteSpace: 'nowrap', padding: '7px 14px', fontWeight: '500' 
                        }}
                    >
                        <i className="fas fa-table mr-1"></i> {isAdmin ? "Ma Trận Tổng Thể" : "Ma Trận Tháng"}
                    </Button>
                    {(isAdmin || isStudent) && (
                        <Button
                            variant={viewMode === 'student' ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => setViewMode('student')}
                            style={{ 
                                borderTopRightRadius: (isAdmin && !isInstructor) || isStudent ? '6px' : 0,
                                borderBottomRightRadius: (isAdmin && !isInstructor) || isStudent ? '6px' : 0,
                                borderRadius: isAdmin ? 0 : undefined,
                                whiteSpace: 'nowrap', padding: '7px 14px', fontWeight: '500' 
                            }}
                        >
                            <i className="fas fa-graduation-cap mr-1"></i> {isStudent ? "Lịch Học Chi Tiết Của Tôi" : "Lộ Trình Theo Học Viên"}
                        </Button>
                    )}
                    {(isAdmin || isInstructor) && (
                        <Button
                            variant={viewMode === 'instructor' ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => setViewMode('instructor')}
                            style={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px', whiteSpace: 'nowrap', padding: '7px 14px', fontWeight: '500' }}
                        >
                            <i className="fas fa-user-tie mr-1"></i> {isInstructor ? "Lịch Giảng Dạy Của Tôi" : "Lịch Theo Giảng Viên"}
                        </Button>
                    )}
                </div>

                <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    {isAdmin && (
                        <Button
                            variant="primary"
                            size="sm"
                            className="shadow-sm font-weight-bold"
                            onClick={() => setShowCreateModal(true)}
                        >
                            <i className="fas fa-plus-circle mr-1"></i> Phân Lịch Học
                        </Button>
                    )}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRefetchAll}
                        title="Tải lại dữ liệu"
                    >
                        <i className="fas fa-sync-alt mr-1"></i> Tải lại
                    </Button>
                </div>
            </div>

            {/* VIEW 1: DETAILED LIST VIEW */}
            {viewMode === 'list' && (
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-0">
                        <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 360px)' }}>
                            <Table bordered hover size="sm" className="mb-0 align-middle">
                                <thead className="thead-light">
                                    <tr>
                                        <th style={{ width: 50 }} className="text-center">#</th>
                                        <th style={{ minWidth: 150 }}>Học viên</th>
                                        <th style={{ minWidth: 220 }}>Khoa / Đơn vị thực hành</th>
                                        <th style={{ minWidth: 200 }}>Thời gian đào tạo</th>
                                        <th style={{ minWidth: 160 }}>Giảng viên phụ trách</th>
                                        <th style={{ minWidth: 130 }}>Trình độ / Giai đoạn</th>
                                        <th style={{ minWidth: 120 }}>Ghi chú</th>
                                        <th className="text-center" style={{ width: 110 }}>Trạng thái</th>
                                        {isAdmin && <th className="text-center" style={{ width: 90 }}>Thao tác</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {isScheduleLoading ? (
                                        <tr>
                                            <td colSpan={isAdmin ? 9 : 8} className="text-center py-4">
                                                <span className="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                                                Đang tải danh sách phân lịch...
                                            </td>
                                        </tr>
                                    ) : groupedFilteredScheduleList.length === 0 ? (
                                        <tr>
                                            <td colSpan={isAdmin ? 9 : 8} className="text-center py-5 text-muted">
                                                <i className="far fa-calendar-times fa-3x mb-3 text-secondary d-block" style={{ opacity: 0.4 }}></i>
                                                Chưa có lịch học nào được phân công.
                                                {isAdmin && (
                                                    <div className="mt-2">
                                                        <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                                                            <i className="fas fa-plus mr-1"></i> Phân lịch học ngay
                                                        </Button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ) : (
                                        groupedFilteredScheduleList.map((item, idx) => {
                                            const gvName = item.giangVienTen || (item.giangViens && item.giangViens[0]?.giangVienTen) || '-';

                                            return (
                                                <tr key={item.key || item.id || idx}>
                                                    <td className="text-center text-muted">{idx + 1}</td>
                                                    <td className="font-weight-bold text-dark">
                                                        <i className="fas fa-user-graduate text-muted mr-2"></i>
                                                        {item.hocVienTen || 'Chưa rõ tên'}
                                                    </td>
                                                    <td style={{ minWidth: 220, whiteSpace: 'normal' }}>
                                                        <span className="font-weight-500 text-primary">
                                                            <i className="fas fa-hospital-alt mr-1"></i>
                                                            {item.donVi || 'Chưa chọn khoa'}
                                                        </span>
                                                    </td>
                                                    <td style={{ minWidth: 200, whiteSpace: 'normal' }}>
                                                        {item.items.length === 1 ? (
                                                            item.isSingleRange ? (
                                                                <Badge variant="light" className="border px-2 py-1 text-dark" style={{ fontSize: '12px' }}>
                                                                    <i className="far fa-calendar-alt text-primary mr-1"></i>
                                                                    {moment(item.items[0].ngayBatDau).format('DD/MM/YYYY')} &rarr; {moment(item.items[0].ngayKetThuc).format('DD/MM/YYYY')}
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="light" className="border px-2 py-1 text-dark" style={{ fontSize: '12px' }}>
                                                                    <i className="far fa-calendar-alt text-primary mr-1"></i>
                                                                    {item.dates[0]}
                                                                </Badge>
                                                            )
                                                        ) : (
                                                            <div className="d-flex flex-wrap align-items-center" style={{ gap: '4px' }}>
                                                                {item.dates.map((d, dIdx) => (
                                                                    <span key={dIdx} className="badge badge-light border text-primary" style={{ fontSize: '11px', padding: '3px 6px', display: 'inline-flex', alignItems: 'center' }}>
                                                                        <i className="far fa-calendar-check mr-1 text-success"></i>{d}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {gvName !== '-' ? (
                                                            <span className="text-dark">
                                                                <i className="fas fa-chalkboard-teacher text-info mr-1"></i>
                                                                {gvName}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted small">Chưa phân công</span>
                                                        )}
                                                    </td>
                                                    <td>{item.trinhDo || '-'}</td>
                                                    <td className="text-muted small" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {item.ghiChu || '-'}
                                                    </td>
                                                    <td className="text-center">
                                                        {item.trangThai === 1 ? (
                                                            <Badge variant="success">Đã xác nhận</Badge>
                                                        ) : item.trangThai === 2 ? (
                                                            <Badge variant="warning">Cảnh báo</Badge>
                                                        ) : (
                                                            <Badge variant="secondary">Nháp</Badge>
                                                        )}
                                                    </td>
                                                    {isAdmin && (
                                                        <td className="text-center">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="p-1 mr-1"
                                                                title="Sửa lịch"
                                                                onClick={() => handleOpenEdit(item.representative)}
                                                            >
                                                                <i className="fas fa-pencil-alt"></i>
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                className="p-1"
                                                                title="Xóa lịch"
                                                                onClick={() => handleDeleteClick(item.representative)}
                                                            >
                                                                <i className="fas fa-trash-alt"></i>
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* VIEW 2: MATRIX VIEW */}
            {viewMode === 'matrix' && (
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white py-2">
                        <Row className="align-items-center justify-content-between">
                            <Col md={7} className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-0 mr-2 font-weight-bold small">Tháng:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        as="select"
                                        style={{ width: 105 }}
                                        value={thang}
                                        onChange={(e) => setThang(Number(e.target.value))}
                                        className="mr-2"
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>Tháng {m}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Label className="mb-0 mr-2 font-weight-bold small">Năm:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        style={{ width: 85 }}
                                        value={nam}
                                        onChange={(e) => setNam(Number(e.target.value))}
                                    />
                                </div>

                                {isAdmin && (
                                    <div className="btn-group btn-group-sm ml-md-2" role="group">
                                        <button
                                            type="button"
                                            className={`btn ${matrixTarget === 'student' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => setMatrixTarget('student')}
                                            style={{ fontWeight: 500 }}
                                        >
                                            <i className="fas fa-user-graduate mr-1"></i> Theo Học Viên
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn ${matrixTarget === 'instructor' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => setMatrixTarget('instructor')}
                                            style={{ fontWeight: 500 }}
                                        >
                                            <i className="fas fa-chalkboard-teacher mr-1"></i> Theo Giảng Viên
                                        </button>
                                    </div>
                                )}
                            </Col>
                            <Col md={5} className="text-right small text-muted mt-2 mt-md-0">
                                <span className="mr-3">
                                    <span className="badge badge-success mr-1">&nbsp;</span> {matrixTarget === 'instructor' && isAdmin ? "Có lịch hướng dẫn" : "Có lịch học"}
                                </span>
                                <span>
                                    <span className="badge badge-danger mr-1">&nbsp;</span> Nghỉ
                                </span>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 360px)' }}>
                            <Table bordered hover size="sm" className="mb-0 text-center text-nowrap">
                                <thead className="thead-light">
                                    <tr>
                                        <th style={{ position: 'sticky', left: 0, zIndex: 10, background: '#f8f9fa', width: 45 }}>#</th>
                                        <th style={{ position: 'sticky', left: 45, zIndex: 10, background: '#f8f9fa', minWidth: 200 }} className="text-left">
                                            {isAdmin && matrixTarget === 'instructor' ? 'Họ và tên giảng viên' : 'Họ và tên học viên'}
                                        </th>
                                        {matrixDates.map((dStr, idx) => {
                                            const mDate = moment(dStr);
                                            const isWeekend = mDate.day() === 0 || mDate.day() === 6;
                                            return (
                                                <th key={idx} style={{ minWidth: 55, backgroundColor: isWeekend ? '#fff3cd' : undefined }}>
                                                    <small className="font-weight-bold">{mDate.format('DD')}</small>
                                                    <br />
                                                    <span style={{ fontSize: '10px', color: '#6c757d' }}>{mDate.format('dd')}</span>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {isMatrixLoading ? (
                                        <tr>
                                            <td colSpan={matrixDates.length + 2} className="py-4">
                                                <span className="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                                                Đang tải ma trận lịch học...
                                            </td>
                                        </tr>
                                    ) : matrixRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={matrixDates.length + 2} className="py-4 text-muted">
                                                Chưa có dữ liệu lịch {isAdmin && matrixTarget === 'instructor' ? 'giảng dạy' : 'học'} cho tháng {thang}/{nam}.
                                            </td>
                                        </tr>
                                    ) : (
                                        matrixRows.map((row, rIdx) => {
                                            const isRowInstructor = isAdmin && matrixTarget === 'instructor';
                                            const rowTitle = row.hocVienTen || row.tutorTen || 'Chưa rõ';

                                            return (
                                                <tr key={rIdx}>
                                                    <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 5 }}>{rIdx + 1}</td>
                                                    <td style={{ position: 'sticky', left: 45, background: '#fff', zIndex: 5 }} className="text-left font-weight-bold">
                                                        <i className={`${isRowInstructor ? 'fas fa-chalkboard-teacher text-info' : 'fas fa-user-graduate text-muted'} mr-2`}></i>
                                                        {rowTitle}
                                                    </td>
                                                    {matrixDates.map((dStr, dIdx) => {
                                                        const cell = row.scheduleByDate ? row.scheduleByDate[dStr] : null;
                                                        let isNghi = false;
                                                        let isHoc = false;
                                                        let maKhoa = '';
                                                        let fullDonVi = '';
                                                        let gvTen = '';
                                                        let hocVienNames = '';
                                                        let trinhDo = '';
                                                        let ghiChu = '';

                                                        if (cell) {
                                                            if (typeof cell === 'string') {
                                                                if (cell === 'Nghỉ') isNghi = true;
                                                                else if (cell !== '-') {
                                                                    isHoc = true;
                                                                    maKhoa = cell;
                                                                    fullDonVi = cell;
                                                                }
                                                            } else {
                                                                if (cell.trangThai === 'Nghi') isNghi = true;
                                                                else if (cell.trangThai === 'Hoc' || cell.maDonVi || cell.donVi || cell.giangVienTen || cell.hocVienTen) {
                                                                    isHoc = true;
                                                                    maKhoa = cell.maDonVi || cell.donVi || '';
                                                                    fullDonVi = cell.donVi || cell.maDonVi || '';
                                                                    gvTen = cell.giangVienTen || '';
                                                                    hocVienNames = cell.hocVienTen || '';
                                                                    trinhDo = cell.trinhDo || '';
                                                                    ghiChu = cell.ghiChu || '';
                                                                }
                                                            }
                                                        }

                                                        let cellBg = "#fff";
                                                        if (isNghi) cellBg = "#f8d7da";
                                                        else if (isHoc) cellBg = isRowInstructor ? "#e3f2fd" : "#e8f5e9";

                                                        if (isNghi) {
                                                            return (
                                                                <td key={dIdx} style={{ backgroundColor: cellBg, padding: '6px 2px', verticalAlign: 'middle' }}>
                                                                    <span className="text-danger font-weight-bold" style={{ fontSize: '11px' }}>Nghỉ</span>
                                                                </td>
                                                            );
                                                        }

                                                        if (isHoc) {
                                                            const popover = (
                                                                <Popover id={`pop-${rIdx}-${dIdx}`} style={{ maxWidth: '330px', zIndex: 1060, boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}>
                                                                    <Popover.Title as="h6" className="font-weight-bold bg-light py-1 px-2 border-bottom mb-0 text-dark d-flex justify-content-between align-items-center">
                                                                        <span>
                                                                            <i className={`${isRowInstructor ? 'fas fa-chalkboard-teacher text-info' : 'far fa-calendar-alt text-primary'} mr-1`}></i> 
                                                                            {rowTitle}
                                                                        </span>
                                                                        <small className="text-muted">{moment(dStr).format('DD/MM/YYYY')}</small>
                                                                    </Popover.Title>
                                                                    <Popover.Content className="p-2 text-left small" style={{ lineHeight: 1.6, color: '#212529' }}>
                                                                        <div className="mb-1">
                                                                            🏥 <strong>Khoa / Đơn vị:</strong> {fullDonVi || maKhoa || 'Chưa rõ'}
                                                                        </div>
                                                                        {isRowInstructor ? (
                                                                            <div className="mb-1">
                                                                                🎓 <strong>Học viên hướng dẫn:</strong> {hocVienNames || 'Chưa rõ học viên'}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="mb-1">
                                                                                👨‍🏫 <strong>Giảng viên:</strong> {gvTen || 'Chưa phân công'}
                                                                            </div>
                                                                        )}
                                                                        {trinhDo && (
                                                                            <div className="mb-1">
                                                                                📝 <strong>Nội dung / Giai đoạn:</strong> {trinhDo}
                                                                            </div>
                                                                        )}
                                                                        {ghiChu && (
                                                                            <div className="text-muted font-italic">
                                                                                📋 <strong>Ghi chú:</strong> {ghiChu}
                                                                            </div>
                                                                        )}
                                                                    </Popover.Content>
                                                                </Popover>
                                                            );

                                                            return (
                                                                <OverlayTrigger key={dIdx} trigger={['hover', 'focus']} placement="auto" overlay={popover}>
                                                                    <td
                                                                        style={{ backgroundColor: cellBg, padding: '6px 2px', verticalAlign: 'middle', cursor: 'pointer' }}
                                                                    >
                                                                        <span className={`font-weight-bold ${isRowInstructor ? 'text-info' : 'text-primary'}`} style={{ fontSize: '11px' }}>
                                                                            {maKhoa || <i className="fas fa-check text-success font-weight-bold"></i>}
                                                                        </span>
                                                                    </td>
                                                                </OverlayTrigger>
                                                            );
                                                        }

                                                        return (
                                                            <td
                                                                key={dIdx}
                                                                style={{ backgroundColor: cellBg, padding: '6px 2px', verticalAlign: 'middle' }}
                                                            >
                                                                <span className="text-muted" style={{ opacity: 0.35 }}>-</span>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* VIEW 3: STUDENT TIMELINE VIEW */}
            {viewMode === 'student' && (
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white py-2">
                        <Row className="align-items-center justify-content-between">
                            <Col md={7} className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                {isStudent ? (
                                    <div className="d-flex align-items-center mr-3">
                                        <Badge variant="primary" className="p-2 mr-2" style={{ fontSize: '13px' }}>
                                            <i className="fas fa-user-graduate mr-1"></i> Học viên
                                        </Badge>
                                        <span className="font-weight-bold text-dark" style={{ fontSize: '14px' }}>
                                            {currentStudent?.hoTen || currentUser?.name || 'Học viên'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center mr-3" style={{ minWidth: 260 }}>
                                        <span className="font-weight-bold text-dark mr-2 text-nowrap small">Học Viên:</span>
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <Select
                                                size="sm"
                                                options={studentOptions}
                                                value={studentOptions.find(opt => opt.value === selectedStudentId) || null}
                                                onChange={(opt) => setSelectedStudentId(opt ? opt.value : '')}
                                                placeholder="Tìm & chọn học viên..."
                                                isClearable={false}
                                                classNamePrefix="select-sm"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '32px',
                                                        height: '32px',
                                                        fontSize: '13px',
                                                        borderRadius: '4px'
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        padding: '0 8px'
                                                    }),
                                                    input: (base) => ({
                                                        ...base,
                                                        margin: '0',
                                                        padding: '0'
                                                    }),
                                                    indicatorsContainer: (base) => ({
                                                        ...base,
                                                        height: '32px'
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-0 mr-1 font-weight-bold small">Tháng:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        as="select"
                                        style={{ width: 100 }}
                                        value={thang}
                                        onChange={(e) => setThang(Number(e.target.value))}
                                        className="mr-2"
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>Tháng {m}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Label className="mb-0 mr-1 font-weight-bold small">Năm:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        style={{ width: 85 }}
                                        value={nam}
                                        onChange={(e) => setNam(Number(e.target.value))}
                                    />
                                </div>
                            </Col>
                            <Col md={5} className="text-md-right text-muted small mt-2 mt-md-0">
                                Tổng cộng: <strong>{groupedStudentSchedules.length}</strong> giai đoạn đào tạo
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body>
                        {groupedStudentSchedules.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="fas fa-user-clock fa-3x mb-3 text-secondary" style={{ opacity: 0.4 }}></i>
                                <p>Học viên này chưa có giai đoạn đào tạo nào được phân lịch.</p>
                                {isAdmin && (
                                    <Button variant="outline-primary" size="sm" onClick={() => setShowCreateModal(true)}>
                                        <i className="fas fa-plus mr-1"></i> Phân lịch cho học viên này
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="timeline-container px-3">
                                {groupedStudentSchedules.map((sch, sIdx) => {
                                    const gvName = sch.giangVienTen || (sch.giangViens && sch.giangViens[0]?.giangVienTen) || 'Chưa phân công';

                                    return (
                                        <div key={sch.key || sIdx} className="d-flex mb-3 pb-3 border-bottom">
                                            <div className="mr-3 text-center" style={{ minWidth: 60 }}>
                                                <Badge variant="primary" className="p-2" style={{ borderRadius: '50%', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {sIdx + 1}
                                                </Badge>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <span className="font-weight-bold text-primary" style={{ fontSize: '15px' }}>
                                                        <i className="fas fa-hospital mr-1"></i> {sch.donVi || 'Chưa chọn khoa'}
                                                    </span>
                                                    {sch.trangThai === 1 ? (
                                                        <Badge variant="success">Đã xác nhận</Badge>
                                                    ) : sch.trangThai === 2 ? (
                                                        <Badge variant="warning">Cảnh báo</Badge>
                                                    ) : (
                                                        <Badge variant="secondary">Nháp</Badge>
                                                    )}
                                                </div>
                                                <div className="text-muted small mb-2">
                                                    {sch.items.length === 1 ? (
                                                        sch.isSingleRange ? (
                                                            <span className="font-weight-500">
                                                                <i className="far fa-calendar-alt text-primary mr-1"></i>
                                                                {moment(sch.items[0].ngayBatDau).format('DD/MM/YYYY')} &rarr; {moment(sch.items[0].ngayKetThuc).format('DD/MM/YYYY')}
                                                            </span>
                                                        ) : (
                                                            <span className="font-weight-500">
                                                                <i className="far fa-calendar-alt text-primary mr-1"></i>
                                                                {sch.dates[0]}
                                                            </span>
                                                        )
                                                    ) : (
                                                        <div className="d-flex flex-wrap align-items-center" style={{ gap: '4px' }}>
                                                            {sch.dates.map((d, dIdx) => (
                                                                <span key={dIdx} className="badge badge-light border text-primary" style={{ fontSize: '11px', padding: '3px 6px', display: 'inline-flex', alignItems: 'center' }}>
                                                                    <i className="far fa-calendar-check mr-1 text-success"></i>
                                                                    {d}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-dark small mb-1">
                                                    <i className="fas fa-chalkboard-teacher mr-1 text-success"></i>
                                                    Giảng viên phụ trách: <strong>{gvName}</strong>
                                                </div>
                                                {sch.trinhDo && (
                                                    <div className="text-dark small mb-1">
                                                        <i className="fas fa-tasks mr-1 text-warning"></i>
                                                        Nội dung/Trình độ: {sch.trinhDo}
                                                    </div>
                                                )}
                                                {sch.ghiChu && (
                                                    <div className="text-muted small italic">
                                                        <i className="far fa-sticky-note mr-1"></i>
                                                        Ghi chú: {sch.ghiChu}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* VIEW 4: INSTRUCTOR AGENDA VIEW */}
            {viewMode === 'instructor' && (
                <Card className="shadow-sm border-0">
                    <Card.Header className="bg-white py-2">
                        <Row className="align-items-center justify-content-between">
                            <Col md={7} className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                {isInstructor ? (
                                    <div className="d-flex align-items-center mr-3">
                                        <Badge variant="success" className="p-2 mr-2" style={{ fontSize: '13px' }}>
                                            <i className="fas fa-chalkboard-teacher mr-1"></i> Giảng viên
                                        </Badge>
                                        <span className="font-weight-bold text-dark" style={{ fontSize: '14px' }}>
                                            {currentTutor?.fullName || currentUser?.name || 'Giảng viên'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center mr-3" style={{ minWidth: 260 }}>
                                        <span className="font-weight-bold text-dark mr-2 text-nowrap small">Giảng Viên:</span>
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <Select
                                                size="sm"
                                                options={instructorOptions}
                                                value={instructorOptions.find(opt => opt.value === selectedInstructorId) || null}
                                                onChange={(opt) => setSelectedInstructorId(opt ? opt.value : '')}
                                                placeholder="Tìm & chọn giảng viên..."
                                                isClearable={false}
                                                classNamePrefix="select-sm"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '32px',
                                                        height: '32px',
                                                        fontSize: '13px',
                                                        borderRadius: '4px'
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        padding: '0 8px'
                                                    }),
                                                    input: (base) => ({
                                                        ...base,
                                                        margin: '0',
                                                        padding: '0'
                                                    }),
                                                    indicatorsContainer: (base) => ({
                                                        ...base,
                                                        height: '32px'
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-0 mr-1 font-weight-bold small">Tháng:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        as="select"
                                        style={{ width: 100 }}
                                        value={thang}
                                        onChange={(e) => setThang(Number(e.target.value))}
                                        className="mr-2"
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>Tháng {m}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Label className="mb-0 mr-1 font-weight-bold small">Năm:</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        style={{ width: 85 }}
                                        value={nam}
                                        onChange={(e) => setNam(Number(e.target.value))}
                                    />
                                </div>
                            </Col>
                            <Col md={5} className="text-md-right text-muted small mt-2 mt-md-0">
                                Tổng số lượt phân công: <strong>{groupedInstructorSchedules.length}</strong>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table bordered hover size="sm" className="mb-0 align-middle">
                                <thead className="thead-light">
                                    <tr>
                                        <th style={{ width: 50 }} className="text-center">#</th>
                                        <th style={{ minWidth: 160 }}>Học viên hướng dẫn</th>
                                        <th style={{ minWidth: 220 }}>Khoa / Phòng</th>
                                        <th style={{ minWidth: 200 }}>Thời gian</th>
                                        <th style={{ minWidth: 130 }}>Trình độ / Giai đoạn</th>
                                        <th style={{ minWidth: 120 }}>Ghi chú</th>
                                        {isAdmin && <th className="text-center" style={{ width: 90 }}>Thao tác</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedInstructorSchedules.length === 0 ? (
                                        <tr>
                                            <td colSpan={isAdmin ? 7 : 6} className="text-center py-5 text-muted">
                                                <i className="fas fa-calendar-check fa-3x mb-3 text-secondary" style={{ opacity: 0.4 }}></i>
                                                <p>Giảng viên này hiện chưa được phân công hướng dẫn buổi học nào.</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        groupedInstructorSchedules.map((item, idx) => {
                                            return (
                                                <tr key={item.key || item.id || idx}>
                                                    <td className="text-center text-muted">{idx + 1}</td>
                                                    <td className="font-weight-bold text-dark">
                                                        <i className="fas fa-user-graduate text-primary mr-2"></i>
                                                        {item.hocVienTen || 'Chưa rõ tên'}
                                                    </td>
                                                    <td style={{ minWidth: 220, whiteSpace: 'normal' }}>{item.donVi || '-'}</td>
                                                    <td style={{ minWidth: 200, whiteSpace: 'normal' }}>
                                                        {item.items.length === 1 ? (
                                                            item.isSingleRange ? (
                                                                <Badge variant="light" className="border px-2 py-1 text-dark">
                                                                    {moment(item.items[0].ngayBatDau).format('DD/MM/YYYY')} &rarr; {moment(item.items[0].ngayKetThuc).format('DD/MM/YYYY')}
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="light" className="border px-2 py-1 text-dark">
                                                                    {item.dates[0]}
                                                                </Badge>
                                                            )
                                                        ) : (
                                                            <div className="d-flex flex-wrap align-items-center" style={{ gap: '4px' }}>
                                                                {item.dates.map((d, dIdx) => (
                                                                    <span key={dIdx} className="badge badge-light border text-primary" style={{ fontSize: '11px', padding: '3px 6px', display: 'inline-flex', alignItems: 'center' }}>
                                                                        <i className="far fa-calendar-check mr-1 text-success"></i>{d}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{item.trinhDo || '-'}</td>
                                                    <td className="text-muted small">{item.ghiChu || '-'}</td>
                                                    {isAdmin && (
                                                        <td className="text-center">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="p-1 mr-1"
                                                                title="Sửa"
                                                                onClick={() => handleOpenEdit(item.representative)}
                                                            >
                                                                <i className="fas fa-pencil-alt"></i>
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                className="p-1"
                                                                title="Xóa"
                                                                onClick={() => handleDeleteClick(item.representative)}
                                                            >
                                                                <i className="fas fa-trash-alt"></i>
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* MODAL: CREATE SCHEDULE */}
            <CreateScheduleThucHanhModal
                show={showCreateModal}
                onHide={() => {
                    setShowCreateModal(false);
                    setQuickCreateState({ studentId: null, date: null });
                }}
                lopId={lopId}
                studentsData={studentsData}
                donViList={donViList}
                initialStudentId={quickCreateState.studentId}
                initialDate={quickCreateState.date}
                onSuccess={() => {
                    refetchSchedule();
                    refetchAdminMatrix();
                    refetchStudentMatrix();
                    refetchInstructorMatrix();
                    queryClient.invalidateQueries(['giangViensThucHanh', lopId]);
                    queryClient.invalidateQueries(['lopHuongDanThucHanhDetail', lopId]);
                }}
            />

            {/* MODAL: EDIT SCHEDULE */}
            <EditScheduleThucHanhModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                }}
                item={editingItem}
                donViList={donViList}
                onSuccess={() => {
                    refetchSchedule();
                    refetchAdminMatrix();
                    refetchStudentMatrix();
                    refetchInstructorMatrix();
                    queryClient.invalidateQueries(['giangViensThucHanh', lopId]);
                    queryClient.invalidateQueries(['lopHuongDanThucHanhDetail', lopId]);
                }}
            />



            {/* MODAL: CONFIRM DELETE */}
            <ConfirmModal
                isOpen={showDeleteModal}
                confirmType="delete"
                title="Xác nhận xóa lịch học"
                message={`Bạn có chắc chắn muốn xóa dòng lịch học của học viên "${itemToDelete?.hocVienTen}" tại đơn vị "${itemToDelete?.donVi || 'Chưa rõ'}"?`}
                handleAccept={handleConfirmDelete}
                handleCancel={() => {
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                }}
            />
        </div>
    );
};

export default ScheduleThucHanhTab;
